import { mapApiCargoOnboardToForm } from "@features/vessel-call/cargoOnboard";
import {
  FORM_FIELDS,
  methodAtImo2020ComplianceValues,
  NATURE_OF_CALL,
  NATURE_OF_CALL_SPECIFY,
  normalizeCallAdditionalDocumentType,
} from "@features/vessel-call/create/constants";
import type { VesselCallFormState } from "@features/vessel-call/create/store/useVesselCallForm.store";

export type UploadItem = { name: string; url: string; type: string };

export const DOC_UPLOAD_FIELDS = [
  FORM_FIELDS.COMPLETED_SIGNED_DOCUMENT_URL,
  FORM_FIELDS.CARGO_DECLARATION_DOCUMENT_URL,
  FORM_FIELDS.DEFICIENCIES_DOCUMENTS_URL,
  FORM_FIELDS.LAST_PORT_CLEARANCE_DOCUMENT_URL,
  FORM_FIELDS.FLAG_STATE_PERMISSION_DOCUMENT_URL,
  FORM_FIELDS.ARM_AMMUNITION_PROOF_DOCUMENT_URL,
  FORM_FIELDS.SHIPS_STORE_DECLARATION_DOCUMENT_URL,
  FORM_FIELDS.DANGEROUS_GOODS_MANIFEST_DOCUMENT_URL,
  FORM_FIELDS.CREWS_EFFECTS_DECLARATION_DOCUMENT_URL,
] as const;

const numToStr = (v: unknown): string | undefined => {
  if (v === null || v === undefined) return "";
  return String(v);
};

const inferMimeFromUrl = (url: string): string => {
  const u = (url || "").toLowerCase();
  if (u.endsWith(".pdf")) return "application/pdf";
  if (u.endsWith(".jpg") || u.endsWith(".jpeg")) return "image/jpeg";
  if (u.endsWith(".png")) return "image/png";
  if (u.endsWith(".gif")) return "image/gif";
  if (u.endsWith(".bmp")) return "image/bmp";
  if (u.endsWith(".doc")) return "application/msword";
  if (u.endsWith(".docx")) {
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  }
  if (u.endsWith(".xls")) return "application/vnd.ms-excel";
  if (u.endsWith(".xlsx")) {
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  }
  return "application/octet-stream";
};

const fileNameFromUrl = (url?: string): string => {
  if (!url) return "document";
  const decoded = decodeURIComponent(url.split("?")[0]);
  const parts = decoded.split("/");
  const last = parts[parts.length - 1];
  return last || "document";
};

const wrapDoc = (url?: string, name?: string): UploadItem[] => {
  if (!url) return [];
  const safe = String(url);
  return [
    {
      url: safe,
      name: name || fileNameFromUrl(safe),
      type: inferMimeFromUrl(safe),
    },
  ];
};

const mapAdditionalDocuments = (arr?: any[]) =>
  Array.isArray(arr)
    ? arr.map((d) => ({
        documentUrl: d?.url ?? "",
        documentType: normalizeCallAdditionalDocumentType(d?.type ?? ""),
        otherSpecifyType: d?.specify ?? "",
        documentName: d?.name ?? "",
        documentSummary: d?.summary ?? "",
        issueDate: typeof d?.issueDate === "string" ? d.issueDate : (d?.issueDate ?? ""),
        expiryDate: d?.expiryDate ?? "",
        fileName: d?.originalFileName ?? "",
        fileFormat: d?.fileFormat ?? "",
      }))
    : [];

const getEntryOrder = (entry: any, fallbackIndex: number) => {
  const order = entry?.no ?? entry?.order;

  return typeof order === "number" && Number.isFinite(order) ? order : fallbackIndex + 1;
};

const mapCrewToStore = (raw: any): VesselCallFormState["crew"] => {
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return list.map((m, idx) => ({
    id: m?.id ?? String(idx),
    order: getEntryOrder(m, idx),
    familyName: m?.familyName ?? "",
    givenName: m?.givenName ?? "",
    rank: m?.rankReferenceCode ?? m?.rankOfRating ?? "",
    nationality: m?.nationality ?? "",
    dateOfBirth: m?.birthDate ?? m?.dateOfBirth ?? "",
    placeOfBirth: m?.birthPlace ?? m?.placeOfBirth ?? "",
    gender: m?.gender ?? "",
    passportNumber: m?.passportNumber ?? "",
    issuingState: m?.identityDocumentIssuingStateReferenceCode ?? m?.issuingState ?? "",
    expiryDate: m?.identityDocumentExpiryDate ?? m?.expiryDate ?? "",
  }));
};

const mapPassengersToStore = (raw: any): VesselCallFormState["passengers"] => {
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return list.map((p, idx) => ({
    id: p?.id ?? String(idx),
    order: getEntryOrder(p, idx),
    familyName: p?.familyName ?? "",
    givenName: p?.givenName ?? "",
    nationality: p?.nationality ?? "",
    dateOfBirth: p?.birthDate ?? p?.dateOfBirth ?? "",
    placeOfBirth: p?.birthPlace ?? p?.placeOfBirth ?? "",
    gender: p?.gender ?? "",
    typeOfIdentityDocument:
      p?.identityOrTravelDocumentTypeReferenceCode ?? p?.typeOfIdentityDocument ?? "",
    identityDocumentNumber:
      p?.identityOrTravelDocumentSerialNumber ?? p?.identityDocumentNumber ?? "",
    issuingState: p?.identityOrTravelDocumentIssuingStateReferenceCode ?? p?.issuingState ?? "",
    expiryDate: p?.identityOrTravelDocumentExpiryDate ?? p?.expiryDate ?? "",
    visaNumberIfAppropriate: p?.visaNumber ?? p?.visaNumberIfAppropriate ?? "",
    portOfEmbarkation: p?.embarkationPortReferenceCode ?? p?.portOfEmbarkation ?? "",
    portOfDisembarkation: p?.disembarkationPortReferenceCode ?? p?.portOfDisembarkation ?? "",
    transitPassenger: Boolean(p?.transitPassenger),
  }));
};

export function mapVesselCallToFormState(vc: any): Partial<VesselCallFormState> {
  const gd = vc?.generalDeclaration ?? {};
  const noc = vc?.natureOfCall ?? {};
  const portCallsRaw = Array.isArray(vc?.portCalls)
    ? vc.portCalls
    : vc?.portCalls
      ? [vc.portCalls]
      : [];

  const crew = mapCrewToStore(vc?.crews);
  const passengers = mapPassengersToStore(vc?.passengers);
  const cargoOnboard = mapApiCargoOnboardToForm(vc?.cargoOnboard);

  const crewCount = gd?.crewNumber ?? (crew.length ? crew.length : undefined);
  const paxCount = gd?.passengersNumber ?? (passengers.length ? passengers.length : undefined);

  return {
    [FORM_FIELDS.VESSEL_ID]: vc?.vesselId ?? "",
    [FORM_FIELDS.SELECT_PORT]: vc?.selectPortReferenceCode ?? "",
    [FORM_FIELDS.EXPECTED_ARRIVAL_LOCATION]: vc?.expectedArrivalLocation ?? "",
    [FORM_FIELDS.PREFERRED_ANCHORAGE_AREA]: vc?.preferredAnchorageArea?.anchorageAreaCode ?? "",
    [FORM_FIELDS.REASON_FOR_ANCHORAGE_PREFERENCE]: vc?.reasonForAnchoragePreference ?? "",
    [FORM_FIELDS.MASTERS_NAME]: vc?.masterName ?? vc?.masterName ?? "",
    [FORM_FIELDS.NAME_OF_MASTER]: gd?.masterName ?? vc?.masterName ?? "",
    [FORM_FIELDS.NATIONALITY]: vc?.nationality ?? "",
    [FORM_FIELDS.ETA]: vc?.eta ?? "",
    [FORM_FIELDS.ETD]: vc?.etd ?? "",
    [FORM_FIELDS.OWNERS_MATTERS_AGENT]: vc?.ownersMattersAgent ?? "",
    [FORM_FIELDS.LAST_PORT]: vc?.lastPortReferenceCode ?? "",
    [FORM_FIELDS.NEXT_PORT]: vc?.nextPortReferenceCode ?? "",
    [FORM_FIELDS.DRAFT_FWD]: vc?.draftFwd ?? undefined,
    [FORM_FIELDS.DRAFT_AFT]: vc?.draftAft ?? undefined,
    [FORM_FIELDS.VESSEL_CONDITION]: vc?.vesselConditionReferenceCode ?? "",
    [FORM_FIELDS.CARGO_ONBOARD]: cargoOnboard,

    [NATURE_OF_CALL.PRIMARY_NATURE_OF_CALL_CODE]: noc.primaryNatureOfCallCode ?? "",
    [NATURE_OF_CALL.PRIMARY_NATURE_OF_CALL_DESC]: noc.primaryNatureOfCallDesc ?? "",
    [NATURE_OF_CALL.CARGO_OPERATION]: Boolean(noc.cargoOperation),
    [NATURE_OF_CALL_SPECIFY.CARGO_OPETATION_SPECIFY]: noc.cargoOperationSpecify ?? "",
    [NATURE_OF_CALL.WASTE_DISPOSAL]: Boolean(noc.wasteDisposal),
    [NATURE_OF_CALL_SPECIFY.WASTE_DISPOSAL_SPECIFY]: noc.wasteDisposalSpecify ?? "",
    [NATURE_OF_CALL.OTHERS]: Boolean(noc.others),
    [NATURE_OF_CALL_SPECIFY.OTHERS_SPECIFY]: noc.otherSpecify ?? "",
    [NATURE_OF_CALL.STS]: Boolean(noc.sts),
    [NATURE_OF_CALL.BUNKERING]: Boolean(noc.bunkering),
    [NATURE_OF_CALL.CREW_CHANGE]: Boolean(noc.crewChange),
    [NATURE_OF_CALL.WAITING_ORDERS]: Boolean(noc.waitingOrders),
    [NATURE_OF_CALL.SURVEY]: Boolean(noc.survey),
    [NATURE_OF_CALL.REPAIRS]: Boolean(noc.repairs),
    [NATURE_OF_CALL.PROJECTS]: Boolean(noc.projects),
    [NATURE_OF_CALL.UNDERWATER_INSPECTION]: Boolean(noc.underwaterInspection),
    [NATURE_OF_CALL.HULL_CLEANING]: Boolean(noc.hullCleaning),
    [NATURE_OF_CALL.STORES_PROVISION]: Boolean(noc.storesProvision),

    [FORM_FIELDS.METHOD_AT_IMO_2020_COMPLIANCE]: vc?.imo2020Compliance
      ? methodAtImo2020ComplianceValues.compliantFuel
      : methodAtImo2020ComplianceValues.closedLoopExhaustGasScrubber,
    [FORM_FIELDS.PORT_FACILITY]: vc?.portFacility ?? "",
    [FORM_FIELDS.PORT_FACILITY_OTHER]: vc?.portFacilitySpecify ?? "",
    [FORM_FIELDS.SECURITY_LEVEL]: vc?.securityLevel ?? "",
    [FORM_FIELDS.SPECIAL_SECURITY_MEASURES]: Boolean(vc?.shipPortSecurityMeasuresTaken),
    [FORM_FIELDS.TAKEN_MEASURES]: vc?.takenMeasures ?? "",
    [FORM_FIELDS.ISSC_IS_VALID]: Boolean(vc?.isscValid),
    [FORM_FIELDS.ISSC_NUMBER]: vc?.isscNumber ?? "",
    [FORM_FIELDS.ISSC_ISSUE_DATE]: vc?.isscIssueDate ?? "",
    [FORM_FIELDS.ISSC_EXPIRY_DATE]: vc?.isscExpiryDate ?? "",
    [FORM_FIELDS.ISSC_ISSUED_BY]: vc?.isscIssuedBy ?? "",
    [FORM_FIELDS.APPROVED_SSP_ONBOARD]: Boolean(vc?.sspOnboardApproved),
    [FORM_FIELDS.SECURITY_GUARDS_ON_BOARD]: Boolean(vc?.securityGuardsOnBoard),
    [FORM_FIELDS.NUMBER_OF_SECURITY_GUARDS]: numToStr(vc?.securityGuardsNumber),

    [FORM_FIELDS.PORT_CALLS]: portCallsRaw.map((x: any, idx: number) => ({
      id: x?.id ?? String(idx),
      port: x?.portReferenceCode ?? "",
      country: x?.countryReferenceCode ?? "",
      securityLevelOfPortCall: x?.securityLevel ?? x?.securityLevelOfPortCall ?? "",
      arrivalDate: x?.arrivalDate ?? "",
      departureDate: x?.departureDate ?? "",
      comments: x?.internalNotes ?? x?.comments ?? "",
    })),

    [FORM_FIELDS.ARM_AMMUNITION_ON_BOARD]: Boolean(vc?.armAmmunitionOnboard),
    [FORM_FIELDS.VESSEL_HAS_DEFICIENCIES]: Boolean(vc?.vesselHasDeficiencies),
    [FORM_FIELDS.DEFICIENCIES]: vc?.deficiencies ?? "",
    [FORM_FIELDS.LAST_PORT_CLEARANCE_ISSUE_DATE]: vc?.lastPortClearanceIssueDate ?? "",
    [FORM_FIELDS.LAST_PORT_CLEARANCE_CLASS_NAME]: vc?.lastPortClearanceClassName ?? "",

    [FORM_FIELDS.NAME_AND_TYPE_OF_SHIP]: gd?.nameAndTypeOfShip ?? "",
    [FORM_FIELDS.IMO_NUMBER]: gd?.imoNumber ?? undefined,
    [FORM_FIELDS.CALL_SIGN]: gd?.callSign ?? "",
    [FORM_FIELDS.VOYAGE_NUMBER]: gd?.voyageNumber ?? "",
    [FORM_FIELDS.PORT_OF_ARRIVAL]: gd?.arrivalPortReferenceCode ?? "",
    [FORM_FIELDS.DATE_TIME_OF_ARRIVAL]: gd?.arrivalDateTime ?? "",
    [FORM_FIELDS.FLAG_STATE_OF_SHIP]: gd?.flagStateOfShip ?? "",
    [FORM_FIELDS.SHIP_AGENT_CONTACT]: gd?.shipAgentDetails ?? "",
    [FORM_FIELDS.GROSS_TONNAGE]: gd?.grossTonnage ?? undefined,
    [FORM_FIELDS.NET_TONNAGE]: gd?.netTonnage ?? undefined,
    [FORM_FIELDS.VOYAGE_PARTICULARS]: gd?.voyageDetails ?? "",
    [FORM_FIELDS.CARGO_DESCRIPTION]: gd?.cargoDescription ?? "",
    [FORM_FIELDS.NUMBER_OF_CREW]: numToStr(crewCount),
    [FORM_FIELDS.NUMBER_OF_PASSENGERS]: numToStr(paxCount),
    [FORM_FIELDS.REMARKS]: gd?.remarks ?? "",
    [FORM_FIELDS.WASTERESIDUES_REQUIREMENTS]: gd?.wasteShipRequirements ?? "",
    [FORM_FIELDS.LAST_PORT_OF_CALL]: gd?.lastPortOfCallReferenceCode ?? "",
    [FORM_FIELDS.CERTIFICATE_OF_REGISTRY]: gd?.registryCertificate ?? "",

    [FORM_FIELDS.COMPLETED_SIGNED_DOCUMENT_URL]: wrapDoc(gd?.completedAndSignedDocumentUrl),
    [FORM_FIELDS.CARGO_DECLARATION_DOCUMENT_URL]: wrapDoc(vc?.cargoDeclarationDocumentUrl),
    [FORM_FIELDS.DEFICIENCIES_DOCUMENTS_URL]: wrapDoc(vc?.deficienciesDocumentsUrl),
    [FORM_FIELDS.LAST_PORT_CLEARANCE_DOCUMENT_URL]: wrapDoc(
      vc?.lastPortClearanceDocumentUrl,
      vc?.lastPortClearanceDocumentName,
    ),
    [FORM_FIELDS.FLAG_STATE_PERMISSION_DOCUMENT_URL]: wrapDoc(vc?.flagStatePermissionDocumentUrl),
    [FORM_FIELDS.ARM_AMMUNITION_PROOF_DOCUMENT_URL]: wrapDoc(vc?.armAmmunitionProofDocumentUrl),
    [FORM_FIELDS.SHIPS_STORE_DECLARATION_DOCUMENT_URL]: wrapDoc(
      vc?.shipsStoresDeclarationDocumentUrl,
    ),
    [FORM_FIELDS.DANGEROUS_GOODS_MANIFEST_DOCUMENT_URL]: wrapDoc(
      vc?.dangerousGoodsManifestDocumentUrl,
    ),
    [FORM_FIELDS.CREWS_EFFECTS_DECLARATION_DOCUMENT_URL]: wrapDoc(
      vc?.crewsEffectsDeclarationDocumentUrl,
    ),

    [FORM_FIELDS.ADDITIONAL_REMARKS]: vc?.additionalRemarks ?? "",
    [FORM_FIELDS.CALL_ADDITIONAL_DOCUMENTS]: mapAdditionalDocuments(vc?.additionalDocuments),

    crew,
    passengers,
  };
}
